import React, { useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router'
import { ThemeProvider as EmotionThemeProvider } from 'emotion-theming'

import { useCanary } from '@bill/canary.react'
import GlobalThemeProvider from '@divvy-web/skylab.globalthemeprovider'
import { brands, fallbackBrand } from '@divvy-web/skylab.themes-emotion'

type BrandKey = keyof typeof brands
type Theme = typeof brands[BrandKey]

export const useTheme = (): BrandKey => {
  const showAireTheme = useCanary('show-skylab-aire-theme')
  const canaryTheme = useCanary('theme') || 'default'
  const [searchParams] = useSearchParams()

  const partnerParam = searchParams.get('partner')
  const sessionTheme = sessionStorage.getItem('partner')
  const activePartner = partnerParam || sessionTheme || 'bill'

  useEffect(() => {
    sessionStorage.setItem('partner', activePartner)
  }, [activePartner])

  // Theme precedence:
  // 1. Aire Theme (if the canary is enabled)
  // 2. Canary Theme (if not 'default')
  // 3. Partner Theme (if set and valid)
  // 4. Default Theme ('bill')
  const theme = useMemo((): BrandKey => {
    const isValidBrand = (key: string): key is BrandKey => key in brands

    if (showAireTheme && isValidBrand('aire')) {
      return 'aire'
    }

    if (canaryTheme !== 'default' && typeof canaryTheme === 'string' && isValidBrand(canaryTheme)) {
      return canaryTheme
    }

    // This might change to a map of partner to partner theme name/brand
    // The partner key might be different from the theme name, so this is just a placeholder for now
    if (activePartner && isValidBrand(activePartner)) {
      return activePartner
    }

    // Absolute Fallback: 'bill'
    // Must be updated to aire once it's generally available,
    // but keeping as bill for now to avoid any potential issues with the new theme rollout
    return 'bill'
  }, [showAireTheme, activePartner, canaryTheme])

  return theme
}

const ThemeProvider = ({ children }: React.PropsWithChildren) => {
  const theme = useTheme()
  const brandTheme = brands[theme] || fallbackBrand

  return (
    <GlobalThemeProvider
      applyResetCss
      applyTokensToDocument
      theme={theme}
    >
      <EmotionThemeProvider<Theme> theme={brandTheme}>{children}</EmotionThemeProvider>
    </GlobalThemeProvider>
  )
}

export default ThemeProvider
